import { PlacePageFragment } from "@/__generated__/graphql";
import { ComponentProps } from "@components/ComponentProps";
import Hero, { CreateHeroProps } from "@components/Hero";
import IconLegend, { IconLegendItem } from "@components/IconLegend";
import { slugify } from "@/lib/utils";
import PageMeta from "@components/PageMeta";
import PreviewEditLink from "@components/PreviewEditLink";
import Slices from "@components/slices";
import { WindowWidth } from "@lib/hooks/WindowWidth";
import { useMobile } from "@lib/hooks/useMobile";
import { GlobalPageProps } from "@lib/utils";
import { GridContainer, GridWrapper } from "@theme/lib";
import { ReactElement } from "react";

export type PlacePageProps = ComponentProps<
  PlacePageFragment
>;

export default function PlacePage({ id, previewEditUrl, latestVersion, versionStatus, published, path, metatags, breadcrumbs, title, content, hero, informationSection, pageUrl }: PlacePageProps & GlobalPageProps): ReactElement<any> {
  const heroProps = hero ? CreateHeroProps(hero) : null;

  const isMobile = useMobile(WindowWidth.Mobile);

  const iconOrder = [
    'status',
    'type-of-place',
    'area',
  ];

  return (
    <>
      <PageMeta
        metatags={metatags}
        breadcrumbs={breadcrumbs}
        path={path}
      />
      {previewEditUrl &&
        <PreviewEditLink url={previewEditUrl} latestVersion={latestVersion} versionStatus={versionStatus} published={published} pageUrl={pageUrl}/>
      }
      {heroProps && <Hero {...heroProps} />}
      <main>
        {informationSection?.tiles?.length &&
          <GridContainer>
            <GridWrapper alignment={"full-width"}>
              <IconLegend
                toggle={isMobile}
                toggleItem="status"
                toggleLabel="Habitat information"
                grid={isMobile ? 1 : 5}
                items={[...informationSection.tiles]
                  .sort((a, b) => {
                    const aIndex = iconOrder.indexOf(slugify(a.label));
                    const bIndex = iconOrder.indexOf(slugify(b.label));
                    return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex);
                  })
                  .map(({ label, value }) => {
                    const slug = slugify(label);
                    const info =
                      slug === 'status' && value
                        ? `<div class="icon-status icon-status-${slugify(value)}"></div>${value}`
                        : (value ?? undefined);

                    return {
                      type: '' as IconLegendItem['type'],
                      label,
                      info,
                    };
                  })}
              />
            </GridWrapper>
          </GridContainer>}
        {content && <Slices {...content} />}
      </main>
    </>
  );
}
